/**
 * Mock Risk / Intelligence scores for Lane planner rate cards (FS2 demo).
 * Not from cargo.one — derived from the quote key (stable across poll/sort
 * re-renders; a new search gets new scores) plus the transit duration, so
 * longer transits tend to score higher Risk.
 *
 * Risk and Intelligence are strongly inversely correlated, but not locked
 * to Intelligence = 100 − Risk (small noise around the inverse).
 *
 * Popover factor breakdowns mirror the Lane Planner HTML prototype (Risk)
 * and the FS2 Intelligence weight table.
 */

export type LaneScoreMode = 'air' | 'ocean';

export interface QuoteMockScores {
  riskPercent: number;
  intelligencePercent: number;
}

export interface ScoreFactor {
  label: string;
  /** Points attributed to this factor (Risk: sum ≈ risk%; Intelligence: sum ≈ intelligence%). */
  valuePercent: number;
  /** FS2 weight share of the Intelligence model (Risk factors omit this). */
  weightPercent?: number;
}

const clamp = (n: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, n));

const hashKey = (key: string): number => {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return hash;
};

/** 0..1 exposure for ocean transit; ~60 days at sea saturates to 1. */
export const oceanRiskDurationFraction = (
  days?: number | null
): number | null => (days == null ? null : clamp(days / 60, 0, 1));

/** 0..1 exposure for air transit (minutes); ~5 days door-to-door saturates. */
export const airRiskDurationFraction = (
  minutes?: number | null
): number | null =>
  minutes == null ? null : clamp(minutes / (5 * 24 * 60), 0, 1);

/**
 * Risk in [10, 90] — 60% driven by transit duration (longer ⇒ riskier) and
 * 40% by key-hash noise; pure hash when duration is unknown. Intelligence is
 * near 100−Risk with ±15pt noise, also clamped to [10, 90].
 */
export const assignMockScores = (
  key: string,
  durationFraction?: number | null
): QuoteMockScores => {
  const hash = hashKey(key);
  const hashUniform = (hash % 1000) / 999; // 0..1
  const riskPercent =
    durationFraction != null
      ? Math.round(10 + 80 * (0.6 * durationFraction + 0.4 * hashUniform))
      : 10 + (hash % 81);
  const noise = ((hash >>> 8) % 31) - 15;
  const intelligencePercent = clamp(100 - riskPercent + noise, 10, 90);
  return {
    riskPercent,
    intelligencePercent,
  };
};

/**
 * Prototype Risk breakdown: shipment delays ~55%, dwell ~30%, heat wave remainder.
 * Values sum exactly to riskPercent.
 */
export const buildRiskFactors = (
  riskPercent: number,
  mode: LaneScoreMode
): ScoreFactor[] => {
  const delayed = Math.round(riskPercent * 0.55);
  const dwell = Math.round(riskPercent * 0.3);
  const heat = Math.max(riskPercent - delayed - dwell, 0);
  return [
    { label: 'Shipment delays', valuePercent: delayed },
    {
      label:
        mode === 'ocean'
          ? 'Transshipment dwell > 2 hr'
          : 'Layover / cross-dock dwell > 2 hr',
      valuePercent: dwell,
    },
    { label: 'Heat wave', valuePercent: heat },
  ];
};

/** FS2 Intelligence model weights (must sum to 100). */
export const INTELLIGENCE_WEIGHTS_AIR: ReadonlyArray<{
  label: string;
  weightPercent: number;
}> = [
  { label: 'Total trip time', weightPercent: 15 },
  { label: 'Number of stops', weightPercent: 20 },
  { label: 'Shipment delays', weightPercent: 15 },
  { label: 'Layover time', weightPercent: 15 },
  { label: 'Predictive weather temperature', weightPercent: 5 },
  { label: 'CO₂ estimate', weightPercent: 5 },
  { label: 'Stability budget', weightPercent: 10 },
  { label: 'Price', weightPercent: 15 },
];

export const INTELLIGENCE_WEIGHTS_OCEAN: ReadonlyArray<{
  label: string;
  weightPercent: number;
}> = [
  { label: 'Total trip time', weightPercent: 15 },
  { label: 'Number of transshipments', weightPercent: 20 },
  { label: 'Shipment delays', weightPercent: 15 },
  { label: 'Port dwell', weightPercent: 15 },
  { label: 'Predictive weather temperature', weightPercent: 5 },
  { label: 'CO₂ estimate', weightPercent: 5 },
  { label: 'Stability budget', weightPercent: 10 },
  { label: 'Price', weightPercent: 15 },
];

/** Index of the shared "Shipment delays" row in both Intelligence weight tables. */
const SHIPMENT_DELAYS_INTEL_INDEX = 2;

/**
 * Intelligence factor rows: FS2 weights plus a per-quote score contribution
 * that sums to intelligencePercent.
 *
 * "Shipment delays" is inversely correlated with the Risk popover's same-named
 * factor (high delay risk ⇒ lower intelligence contribution) — not the same
 * number, since Risk points are "how much delay hurts" and Intelligence points
 * are "how much this factor helps the score."
 */
export const buildIntelligenceFactors = (
  intelligencePercent: number,
  mode: LaneScoreMode,
  key: string,
  riskPercent?: number
): ScoreFactor[] => {
  const weights =
    mode === 'ocean' ? INTELLIGENCE_WEIGHTS_OCEAN : INTELLIGENCE_WEIGHTS_AIR;
  const hash = hashKey(key);
  const delayRiskIntensity =
    riskPercent != null ? clamp(riskPercent / 90, 0, 1) : 0.5;
  // Relative quality per factor in ~0.55–1.45 so rows differ without wild swings.
  const shares = weights.map((w, i) => {
    let relative = 0.55 + (((hash >>> (i * 3)) % 91) / 100) * 0.9;
    if (i === SHIPMENT_DELAYS_INTEL_INDEX) {
      // Inverse of Risk's shipment-delays intensity, plus light hash noise.
      const inverse = 0.35 + (1 - delayRiskIntensity) * 1.0;
      const jitter = (((hash >>> 16) % 21) - 10) / 100; // ±0.10
      relative = clamp(inverse + jitter, 0.3, 1.45);
    }
    return w.weightPercent * relative;
  });
  const shareSum = shares.reduce((a, b) => a + b, 0) || 1;
  const raw = shares.map((s) => (intelligencePercent * s) / shareSum);
  // Largest-remainder: floors first, then +1 to the largest fractions so the
  // rounded contributions sum exactly to intelligencePercent (all ≥ 0).
  const floors = raw.map((n) => Math.floor(Math.max(0, n)));
  let remaining = intelligencePercent - floors.reduce((a, b) => a + b, 0);
  const byFrac = raw
    .map((n, i) => ({ i, frac: Math.max(0, n) - Math.floor(Math.max(0, n)) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  const rounded = [...floors];
  for (const { i } of byFrac) {
    if (remaining <= 0) break;
    rounded[i] += 1;
    remaining -= 1;
  }
  return weights.map((w, i) => ({
    label: w.label,
    weightPercent: w.weightPercent,
    valuePercent: rounded[i],
  }));
};

/** Prototype-ish factor value color (higher contribution ⇒ warmer). */
export const scoreFactorColor = (valuePercent: number): string => {
  if (valuePercent >= 20) return 'var(--danger)';
  if (valuePercent >= 10) return 'var(--warning)';
  return 'var(--text-primary)';
};
